import React from 'react';

import {Text} from '@albert-io/atomic';
import classnames from 'classnames';

interface Props {
  cardClassName?: string;
  iconContent: React.ReactNode;
  iconClassName: string;
  title: string;
  description?: string;
  onClick: () => void;
  /**
   * Marks an in-progress action (e.g. a running sync). The card stays focusable — unlike `disabled`,
   * which drops focus to the body — and `aria-busy` announces the state; clicks are ignored while busy.
   */
  busy?: boolean;
  'data-testid'?: string;
}

export const ActionCard = ({
  cardClassName,
  iconContent,
  iconClassName,
  title,
  description,
  onClick,
  busy = false,
  'data-testid': testId
}: Props) => (
  // Raw button: full-bleed card tile the atomic variants don't model; focus ring mirrors atomic
  // $outline-focus width but uses the semantic brand color (no semantic focus token exists).
  <button
    aria-busy={busy || undefined}
    className={classnames(
      cardClassName,
      'flex w-full min-w-x20 flex-col items-start gap-x2 rounded-x1-half border border-border-primary background-primary p-x3 text-left focus-visible:[outline:3px_solid_var(--color-content-brand)]',
      {
        'cursor-default': busy,
        'cursor-pointer hover:background-primary-hover active:background-primary-pressed': !busy
      }
    )}
    data-testid={testId}
    onClick={() => {
      if (!busy) {
        onClick();
      }
    }}
    type='button'
  >
    <span
      className={`flex h-x5 w-x5 items-center justify-center rounded-x1 text-xl ${iconClassName}`}
    >
      {iconContent}
    </span>
    <div className='flex flex-col gap-x0-quarter'>
      <Text>{title}</Text>
      {description ? (
        <Text color='tertiary' size='s'>
          {description}
        </Text>
      ) : null}
    </div>
  </button>
);
